import type { SavedView, SaveViewPayload, SaveViewResult } from "./DataTable.types";
import { createSaveView, deleteView, getView, listViews, updateView, type SaveViewApiConfig } from "./saveViewApi";

/**
 * A persistence backend for a single screen's saved views.
 *
 * Every method is async so an HTTP-backed adapter (e.g. `createApiViewStore`)
 * can be swapped in later without touching a single call site — the localStorage
 * adapter simply resolves synchronously. `TableViewSelector` depends only on this
 * interface, never on `localStorage` directly.
 */
export interface ViewStore {
  /** All saved views for the screen, in stored order. */
  list(): Promise<SavedView[]>;
  /** A single view by id, or `null` if it isn't stored. */
  get(viewId: string): Promise<SavedView | null>;
  /** The primary view (`is_default: true`) for the screen, or `null`. */
  getActive(): Promise<SavedView | null>;
  /** Upsert by name — mirrors `saveView`'s `{ view, alreadyExists }` shape. */
  save(payload: SaveViewPayload): Promise<SaveViewResult>;
  /** Change a view's name in place. Rejects if the id isn't found. */
  rename(viewId: string, name: string): Promise<SavedView>;
  /** Delete a view. Promotes a new primary if the deleted one was primary. */
  remove(viewId: string): Promise<void>;
  /** Mark a view primary (`is_default: true`), clearing it on all others. */
  setPrimary(viewId: string): Promise<SavedView>;
  /** Clear a view's primary flag. Local-only, like `setPrimary` — there is no confirmed backend field for this. */
  unsetPrimary(viewId: string): Promise<SavedView>;
  /** Remove every saved view for the screen ("Reset saved filters"). */
  reset(): Promise<void>;
  /** Subscribe to same-tab and cross-tab changes; returns an unsubscribe fn. */
  subscribe(listener: () => void): () => void;
}

export type RemoteSaveView = (payload: SaveViewPayload) => Promise<SaveViewResult | void>;

export type LocalViewStoreOptions = {
  /**
   * Optional remote save adapter. When supplied, `save()` calls this first and
   * only mirrors the view into localStorage after the remote save succeeds.
   */
  remoteSave?: RemoteSaveView;
};

export type TableViewSelectorStoreOptions = LocalViewStoreOptions & {
  /**
   * Component-owned API config for Shiprocket saved-view endpoints. This is
   * intentionally used only by TableViewSelector and custom elements so app
   * code does not call saved-view APIs directly.
   */
  saveApi?: SaveViewApiConfig;
};

type ViewStoreRecord = {
  version: 1;
  views: SavedView[];
};

const STORE_PREFIX = "bfrs.savedViews.";
const STORE_VERSION = 1 as const;

const keyFor = (screenName: string) => `${STORE_PREFIX}${encodeURIComponent(screenName)}`;

function hasStorage(): boolean {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch {
    return false;
  }
}

function readRecord(screenName: string): ViewStoreRecord {
  const empty: ViewStoreRecord = { version: STORE_VERSION, views: [] };
  if (!hasStorage()) return empty;
  try {
    const raw = window.localStorage.getItem(keyFor(screenName));
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<ViewStoreRecord>;
    if (!parsed || !Array.isArray(parsed.views)) return empty;
    return { version: STORE_VERSION, views: parsed.views };
  } catch {
    return empty;
  }
}

function writeRecord(screenName: string, views: SavedView[]): void {
  if (!hasStorage()) return;
  try {
    const record: ViewStoreRecord = { version: STORE_VERSION, views };
    window.localStorage.setItem(keyFor(screenName), JSON.stringify(record));
  } catch {
    /* quota / serialization failures are best-effort — swallow */
  }
}

function generateId(): string {
  try {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
      return cryptoApi.randomUUID();
    }
  } catch {
    /* fall through to the non-crypto fallback */
  }
  return `view_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

function normalizeSavedView(view: SavedView | undefined, fallback: SavedView): SavedView {
  return {
    ...fallback,
    ...view,
    view_type: fallback.view_type,
    tab: view?.tab ?? fallback.tab,
    columns: view?.columns ?? fallback.columns,
    filters: view?.filters ?? fallback.filters,
    priority: view?.priority ?? fallback.priority,
    is_default: Boolean(view?.is_default || fallback.is_default),
    is_system_defined: view?.is_system_defined ?? fallback.is_system_defined,
  };
}

function viewsEqual(left: SavedView[], right: SavedView[]): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function ensurePrimary(views: SavedView[]): SavedView[] {
  if (views.length === 0 || views.some((view) => view.is_default)) return views;
  return views.map((view, index) => (index === 0 ? { ...view, is_default: true } : view));
}

const RESERVED_VIEW_NAMES = new Set(["default"]);

/** "Default" is reserved for the unsaved/reset state the selector already renders — a saved view can't reuse it. */
export function isReservedViewName(name: string): boolean {
  return RESERVED_VIEW_NAMES.has(name.trim().toLowerCase());
}

function assertNotReservedViewName(name: string): void {
  if (isReservedViewName(name)) {
    throw new Error(`"${name.trim()}" is a reserved view name. Choose a different name.`);
  }
}

function hasExplicitSaveEndpoint(config: SaveViewApiConfig): boolean {
  return Boolean(config.saveEndpoint || config.endpoint);
}

function hasRemoteViewScope(config: SaveViewApiConfig | undefined): config is SaveViewApiConfig & { viewType: string; tab: string } {
  return Boolean(config?.viewType && config.tab);
}

// ── Change notification (same-tab + cross-tab) ───────────────────────────────
// The browser fires `storage` only in OTHER tabs, so same-tab mutations notify
// their listeners explicitly via `notifyLocal`.

const subscribers = new Map<string, Set<() => void>>();
let storageListenerAttached = false;

function handleStorageEvent(event: globalThis.StorageEvent) {
  if (event.key === null) {
    // localStorage.clear() — key is null; notify every screen.
    subscribers.forEach((set) => set.forEach((listener) => listener()));
    return;
  }
  subscribers.get(event.key)?.forEach((listener) => listener());
}

function ensureStorageListener() {
  if (storageListenerAttached || typeof window === "undefined") return;
  window.addEventListener("storage", handleStorageEvent);
  storageListenerAttached = true;
}

function notifyLocal(storageKey: string) {
  subscribers.get(storageKey)?.forEach((listener) => listener());
}

/**
 * Creates a localStorage-backed {@link ViewStore} scoped to `screenName`.
 * Views for a screen live under `bfrs.savedViews.<screenName>`.
 */
export function createLocalViewStore(screenName: string, options: LocalViewStoreOptions = {}): ViewStore {
  return createViewStore(screenName, options, false);
}

export function createTableViewSelectorStore(screenName: string, options: TableViewSelectorStoreOptions = {}): ViewStore {
  return createViewStore(screenName, options, true);
}

function createViewStore(screenName: string, options: TableViewSelectorStoreOptions = {}, enableSavedViewApi: boolean): ViewStore {
  const storageKey = keyFor(screenName);
  const saveApi = options.saveApi;
  const remoteApi = enableSavedViewApi && hasRemoteViewScope(saveApi) ? saveApi : undefined;
  const remoteSave = options.remoteSave ?? (enableSavedViewApi && saveApi && hasExplicitSaveEndpoint(saveApi) ? createSaveView(saveApi) : undefined);
  const remoteList = remoteApi ? () => listViews(remoteApi) : undefined;
  const remoteGet = remoteApi ? (viewId?: string) => getView({ ...remoteApi, viewId }) : undefined;
  const remoteUpdate = remoteApi ? (payload: SaveViewPayload) => updateView({ ...remoteApi, payload }) : undefined;
  const remoteDelete = remoteApi ? (viewId?: string) => deleteView({ ...remoteApi, viewId }) : undefined;
  let listInFlight: Promise<SavedView[]> | null = null;

  const commit = (views: SavedView[], notify = true) => {
    writeRecord(screenName, views);
    if (notify) notifyLocal(storageKey);
  };

  const normalizeRemoteViews = (remoteViews: SavedView[]) =>
    ensurePrimary(
      remoteViews.map((view, index) =>
        normalizeSavedView(view, {
          view_id: view.view_id || generateId(),
          name: view.name || `View ${index + 1}`,
          view_type: screenName,
          tab: remoteApi?.tab,
          columns: view.columns ?? [],
          filters: view.filters ?? [],
          priority: view.priority ?? index,
          is_default: Boolean(view.is_default),
          is_system_defined: Boolean(view.is_system_defined),
        })
      )
    );

  const mergeRemoteViews = (remoteViews: SavedView[], notify = false) => {
    const current = readRecord(screenName).views;
    const currentById = new Map(current.map((view) => [view.view_id, view]));
    // is_default is local-only: setPrimary/unsetPrimary never reach the backend
    // (no confirmed field for it), so a remote refresh must not resurrect or
    // revert it once the screen already has a local opinion — otherwise every
    // list() call (which fires after EVERY local commit, via the
    // commit -> notify -> subscribe -> loadViews -> list chain) immediately
    // reverts the primary the user just set/unset. Only seed is_default from
    // the remote for views not yet known locally.
    const remote = normalizeRemoteViews(remoteViews).map((view) => {
      const known = currentById.get(view.view_id);
      return known ? { ...view, is_default: known.is_default } : view;
    });
    const remoteHasPrimary = remote.some((view) => view.is_default);
    const localOnly = current
      .filter(
        (view) =>
          !remote.some((remoteView) => remoteView.view_id === view.view_id || remoteView.name.trim() === view.name.trim())
      )
      .map((view) => (remoteHasPrimary ? { ...view, is_default: false } : view));
    const merged = [...remote, ...localOnly];
    // Only force a primary into existence on first hydration (no local record
    // yet). Once the screen has a local record, an all-false state is a
    // deliberate unsetPrimary and must be allowed to stand.
    const next = current.length === 0 ? ensurePrimary(merged) : merged;
    if (!viewsEqual(current, next)) commit(next, notify);
    return next;
  };

  const upsertRemoteActive = (remoteView: SavedView) => {
    const views = readRecord(screenName).views;
    const fallback: SavedView = {
      view_id: remoteView.view_id || generateId(),
      name: remoteView.name || "Primary view",
      view_type: screenName,
      tab: remoteApi?.tab,
      columns: remoteView.columns ?? [],
      filters: remoteView.filters ?? [],
      priority: remoteView.priority ?? 0,
      is_default: true,
      is_system_defined: Boolean(remoteView.is_system_defined),
    };
    const active = normalizeSavedView({ ...remoteView, is_default: true }, fallback);
    const index = views.findIndex((view) => view.view_id === active.view_id || view.name.trim() === active.name.trim());
    const next =
      index >= 0
        ? views.map((view, i) => (i === index ? active : { ...view, is_default: false }))
        : [{ ...active, is_default: true }, ...views.map((view) => ({ ...view, is_default: false }))];
    if (!viewsEqual(views, next)) commit(next, true);
    return active;
  };

  return {
    async list() {
      if (remoteList) {
        if (!listInFlight) {
          listInFlight = remoteList()
            .then((remoteViews) => mergeRemoteViews(remoteViews, true))
            .catch(() => {
              // LocalStorage remains the offline/failure fallback.
              return readRecord(screenName).views;
            })
            .finally(() => {
              listInFlight = null;
            });
        }
        return listInFlight;
      }
      return readRecord(screenName).views;
    },

    async get(viewId) {
      const local = readRecord(screenName).views;
      const localView = local.find((view) => view.view_id === viewId) ?? null;
      if (remoteGet) {
        try {
          // The list endpoint returns only a summary — fetch the view's full
          // detail (columns/filters) so selecting it can apply them.
          const detail = await remoteGet(viewId);
          if (detail) {
            const fallback: SavedView = localView ?? {
              view_id: detail.view_id || viewId,
              name: detail.name || "",
              view_type: screenName,
              tab: remoteApi?.tab,
              columns: [],
              filters: [],
              priority: 0,
              is_default: false,
              is_system_defined: false,
            };
            return normalizeSavedView(detail, fallback);
          }
        } catch {
          // fall through to the local mirror below.
        }
        // Detail fetch already ran — prefer the local summary over a full re-list.
        return localView;
      }
      const views = remoteList ? await this.list() : local;
      return views.find((view) => view.view_id === viewId) ?? null;
    },

    async getActive() {
      if (remoteGet) {
        try {
          const remoteView = await remoteGet();
          return remoteView ? upsertRemoteActive(remoteView) : null;
        } catch {
          // LocalStorage remains the offline/failure fallback.
        }
      }
      return readRecord(screenName).views.find((view) => view.is_default) ?? null;
    },

    async save(payload) {
      assertNotReservedViewName(payload.name);
      const scopedPayload: SaveViewPayload = {
        ...payload,
        view_type: screenName,
        tab: (enableSavedViewApi ? saveApi?.tab : undefined) ?? payload.tab,
        filters: payload.filters ?? [],
      };
      const remoteResult = remoteSave ? await remoteSave(scopedPayload) : undefined;
      const views = readRecord(screenName).views;
      const index = views.findIndex((view) => view.name.trim() === scopedPayload.name.trim());

      if (index >= 0) {
        const fallback: SavedView = {
          ...views[index],
          name: scopedPayload.name,
          view_type: screenName,
          tab: scopedPayload.tab,
          columns: scopedPayload.columns,
          filters: scopedPayload.filters ?? [],
          priority: scopedPayload.priority ?? views[index].priority,
        };
        const updated = normalizeSavedView(remoteResult?.view, fallback);
        const next = views.slice();
        next[index] = updated;
        commit(next);
        return { view: updated, alreadyExists: remoteResult?.alreadyExists ?? true };
      }

      const fallback: SavedView = {
        view_id: generateId(),
        name: scopedPayload.name,
        view_type: screenName,
        tab: scopedPayload.tab,
        columns: scopedPayload.columns,
        filters: scopedPayload.filters ?? [],
        priority: scopedPayload.priority ?? 0,
        // First view for a screen becomes primary so there is always an active view.
        is_default: views.length === 0,
        is_system_defined: false,
      };
      const created = normalizeSavedView(remoteResult?.view, fallback);
      commit([...views, created]);
      return { view: created, alreadyExists: remoteResult?.alreadyExists ?? false };
    },

    async rename(viewId, name) {
      assertNotReservedViewName(name);
      const views = readRecord(screenName).views;
      const index = views.findIndex((view) => view.view_id === viewId);
      if (index < 0) throw new Error(`View "${viewId}" not found for screen "${screenName}"`);
      const fallback: SavedView = { ...views[index], name };
      let remoteView: SavedView | null = null;
      if (remoteUpdate) {
        // The list endpoint returns only a summary (no columns/filters), so fetch
        // the view's full detail first and echo it back with the new name — a
        // partial update payload is rejected by the backend (422).
        const detail = remoteGet ? await remoteGet(viewId).catch(() => null) : null;
        const base = detail ?? views[index];
        remoteView = await remoteUpdate({
          view_id: base.view_id || viewId,
          view_type: screenName,
          tab: remoteApi?.tab,
          name,
          columns: base.columns,
          filters: base.filters,
          priority: base.priority,
        });
      }
      const updated = normalizeSavedView(remoteView ?? undefined, fallback);
      const next = views.slice();
      next[index] = updated;
      commit(next);
      return updated;
    },

    async remove(viewId) {
      const views = readRecord(screenName).views;
      const target = views.find((view) => view.view_id === viewId);
      if (!target) return;
      if (remoteDelete) await remoteDelete(target.view_id);

      let next = views.filter((view) => view.view_id !== viewId);
      // Single-primary invariant: if the primary was removed, promote the first remaining view.
      if (target.is_default && next.length > 0 && !next.some((view) => view.is_default)) {
        next = next.map((view, i) => (i === 0 ? { ...view, is_default: true } : view));
      }
      commit(next);
    },

    async setPrimary(viewId) {
      const views = readRecord(screenName).views;
      const target = views.find((view) => view.view_id === viewId);
      if (!target) throw new Error(`View "${viewId}" not found for screen "${screenName}"`);
      const next = views.map((view) => ({ ...view, is_default: view.view_id === viewId }));
      commit(next);
      return { ...target, is_default: true };
    },

    async unsetPrimary(viewId) {
      // Mirrors setPrimary: local-only, always succeeds. There is no confirmed
      // backend field for clearing is_default on updateview — gating the local
      // commit behind a remote call (as setPrimary itself never does) meant a
      // backend rejection silently blocked the whole action.
      const views = readRecord(screenName).views;
      const target = views.find((view) => view.view_id === viewId);
      if (!target) throw new Error(`View "${viewId}" not found for screen "${screenName}"`);
      const next = views.map((view) => (view.view_id === viewId ? { ...view, is_default: false } : view));
      commit(next);
      return { ...target, is_default: false };
    },

    async reset() {
      if (remoteDelete) await remoteDelete();
      if (hasStorage()) {
        try {
          window.localStorage.removeItem(storageKey);
        } catch {
          /* ignore */
        }
      }
      notifyLocal(storageKey);
    },

    subscribe(listener) {
      ensureStorageListener();
      let set = subscribers.get(storageKey);
      if (!set) {
        set = new Set();
        subscribers.set(storageKey, set);
      }
      set.add(listener);
      return () => {
        const current = subscribers.get(storageKey);
        if (!current) return;
        current.delete(listener);
        if (current.size === 0) subscribers.delete(storageKey);
      };
    },
  };
}

/**
 * A pre-bound save function for a screen's local store, matching the
 * `TableSaveView.onSave` signature.
 *
 * @example
 * const onSave = createLocalSaveView("orders_new");
 * <TableSaveView viewType="orders_new" onSave={onSave} />
 */
export function createLocalSaveView(screenName: string, options?: LocalViewStoreOptions) {
  const store = createLocalViewStore(screenName, options);
  return (payload: SaveViewPayload): Promise<SaveViewResult> => store.save(payload);
}
